/**
 * Interpolation Hermite cubique entre deux points avec leurs vitesses.
 *
 * Reference : https://en.wikipedia.org/wiki/Cubic_Hermite_spline
 *
 * Formes d'Hermite normalisées (t ∈ [0, 1]) :
 *   h00(t) =  2t³ - 3t² + 1
 *   h10(t) =      t³ - 2t² + t
 *   h01(t) = -2t³ + 3t²
 *   h11(t) =      t³ -  t²
 *
 * Position interpolée : p(t) = h00·p0 + h10·dt·m0 + h01·p1 + h11·dt·m1
 * où dt = t1 - t0 et m0/m1 sont les dérivées (vitesses) aux extrémités.
 */

export interface SampledVector {
  epoch_jd: number;
  x_au: number;
  y_au: number;
  z_au: number;
  vx_au_per_day: number;
  vy_au_per_day: number;
  vz_au_per_day: number;
}

export type Vec3 = readonly [number, number, number];

function basis(t: number): { h00: number; h10: number; h01: number; h11: number } {
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    h00: 2 * t3 - 3 * t2 + 1,
    h10: t3 - 2 * t2 + t,
    h01: -2 * t3 + 3 * t2,
    h11: t3 - t2,
  };
}

/** Interpolation Hermite entre deux SampledVector adjacents. */
export function interpolateHermite(
  a: SampledVector,
  b: SampledVector,
  jd: number,
): Vec3 {
  const dt = b.epoch_jd - a.epoch_jd;
  if (dt <= 0) return [a.x_au, a.y_au, a.z_au];
  const t = (jd - a.epoch_jd) / dt;
  if (t <= 0) return [a.x_au, a.y_au, a.z_au];
  if (t >= 1) return [b.x_au, b.y_au, b.z_au];
  const { h00, h10, h01, h11 } = basis(t);
  return [
    h00 * a.x_au + h10 * dt * a.vx_au_per_day + h01 * b.x_au + h11 * dt * b.vx_au_per_day,
    h00 * a.y_au + h10 * dt * a.vy_au_per_day + h01 * b.y_au + h11 * dt * b.vy_au_per_day,
    h00 * a.z_au + h10 * dt * a.vz_au_per_day + h01 * b.z_au + h11 * dt * b.vz_au_per_day,
  ];
}

/** Recherche dichotomique du couple bracketant jd dans un tableau trié par epoch_jd. */
export function bracket(points: SampledVector[], jd: number): [SampledVector, SampledVector] | null {
  if (points.length < 2) return null;
  if (jd <= points[0].epoch_jd) return [points[0], points[1]];
  const last = points.length - 1;
  if (jd >= points[last].epoch_jd) return [points[last - 1], points[last]];
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].epoch_jd <= jd) lo = mid;
    else hi = mid;
  }
  return [points[lo], points[hi]];
}

/** Combine bracket + interpolation. Retourne [0,0,0] si données insuffisantes. */
export function positionAt(points: SampledVector[], jd: number): Vec3 {
  const pair = bracket(points, jd);
  if (!pair) return [0, 0, 0];
  return interpolateHermite(pair[0], pair[1], jd);
}
